import { START_LOCATION } from "vue-router";
import type { NavigationFailure, RouteLocationNormalized, RouterScrollBehavior } from "vue-router";

import { scrollsItself } from "@/utils/scroll-root";

/**
 * Scroll handling for the private shell, whose <main> is the scroller from lg (layouts/Private.vue: an lg:h-dvh column
 * with `<main id="main-content" class="lg:overflow-y-auto">`). vue-router only saves and restores the window's scroll,
 * which is always {0,0} there, so Back landed wherever the previous page's offset clamped, and forward navigations
 * were reset to the top by a 300 ms setTimeout, after the new page had already painted at the old offset.
 *
 * This module records <main>'s offset per history entry and restores it on Back/Forward; a push starts at the top
 * before the first paint. Below lg the private shell scrolls the document (UX roadmap #107), so Back/Forward restores
 * vue-router's saved window position there, with the same wait for a page whose content is still loading. The public
 * layout also scrolls the document, and keeps vue-router's saved position as it is.
 *
 * Crossing lg (a tablet rotated between about 820 and 1180 px) moves the scroll between the document and <main>, and the
 * offset does not carry over: the page lands at the top of its new scroller. Known and accepted.
 */

export const MAIN_SCROLLER_SELECTOR = '.private-shell #main-content';
const PRIVATE_SHELL_SELECTOR = '.private-shell';

/** How long a Back/Forward keeps trying to reach the saved offset while the page's content is still loading. */
export const MAIN_SCROLL_RESTORE_TIMEOUT_MS = 1500;

// Keys are per tab session; the cap only matters for a very long session with many branches.
const MAX_SAVED_OFFSETS = 100;

// Any of these on <main> means the member is scrolling on their own, so a pending restore must stop fighting them.
const USER_SCROLL_INTENT_EVENTS = [ 'wheel', 'touchstart', 'pointerdown', 'keydown' ] as const;

const savedOffsets = new Map< string, number >();

// The history position of the entry on screen. Needed because on a popstate `history.state` already belongs to the
// entry being entered by the time any router hook runs.
let currentPosition: number | null = null;

interface PendingRestore {
    top: number;
    stop: () => void;
}

let pendingRestore: PendingRestore | null = null;

/** The private shell's <main> while it is the scroller (lg and up); null below lg and outside the private shell. */
export function mainScroller(): HTMLElement | null {
    const main = document.querySelector< HTMLElement >( MAIN_SCROLLER_SELECTOR );
    return main && scrollsItself( main ) ? main : null;
}

/** The document's scroller while the private shell lets the document scroll (below lg); null otherwise. */
function shellDocumentScroller(): Element | null {
    if ( !document.querySelector( PRIVATE_SHELL_SELECTOR ) || mainScroller() ) {
        return null;
    }
    return document.scrollingElement ?? document.documentElement;
}

function historyPosition(): number | null {
    const position: unknown = window.history.state?.position;
    return typeof position === 'number' ? position : null;
}

// Same shape as vue-router's own scroll key: the path guards against a stale offset from a discarded forward branch.
function offsetKey( position: number, fullPath: string ): string {
    return `${ position }${ fullPath }`;
}

function saveOffset( key: string, top: number ): void {
    savedOffsets.delete( key );
    savedOffsets.set( key, top );
    if ( savedOffsets.size > MAX_SAVED_OFFSETS ) {
        const oldest = savedOffsets.keys().next().value;
        if ( oldest !== undefined ) {
            savedOffsets.delete( oldest );
        }
    }
}

/*
  A query- or hash-only change (a filter, an open flyout, the carousel's ?webinar=) keeps the page on screen, so it
  never touches the scroll and does not cancel a Back restore still in flight. `to === from` is a repeat click on the
  current link, which still goes to the top as it always did.
*/
function isSamePageNavigation( to: RouteLocationNormalized, from: RouteLocationNormalized ): boolean {
    return from !== START_LOCATION && to !== from && to.path === from.path;
}

function cancelPendingRestore(): void {
    pendingRestore?.stop();
}

function resetScroll( scroller: Element ): void {
    if ( scroller.scrollTop > 0 ) {
        scroller.scrollTop = 0;
    }
}

/*
  Async views and their data usually arrive after the router hands over, so the saved offset is often out of reach on
  the first frame. Keep moving towards it each frame until it is reachable, the member scrolls, another navigation
  starts, or the time runs out. `intentTarget` is where the member's own scrolling shows: <main> itself, or the window
  for the document's scroller.
*/
function restoreScroll( scroller: Element, top: number, intentTarget: EventTarget = scroller ): void {
    if ( top <= 0 ) {
        resetScroll( scroller );
        return;
    }

    const deadline = performance.now() + MAIN_SCROLL_RESTORE_TIMEOUT_MS;
    let frame = 0;

    const stop = (): void => {
        cancelAnimationFrame( frame );
        USER_SCROLL_INTENT_EVENTS.forEach( type => intentTarget.removeEventListener( type, stop ) );
        if ( pendingRestore === restore ) {
            pendingRestore = null;
        }
    };
    const restore: PendingRestore = { top, stop };

    const attempt = (): void => {
        if ( !scroller.isConnected ) {
            stop();
            return;
        }
        const reachable = Math.max( scroller.scrollHeight - scroller.clientHeight, 0 );
        scroller.scrollTop = Math.min( top, reachable );
        if ( reachable >= top || performance.now() >= deadline ) {
            stop();
            return;
        }
        frame = requestAnimationFrame( attempt );
    };

    USER_SCROLL_INTENT_EVENTS.forEach( type => intentTarget.addEventListener( type, stop, { passive: true } ) );
    pendingRestore = restore;
    attempt();
}

/**
 * `router.afterEach` hook. Runs after the URL changes but before the new page is patched in, so <main> still shows the
 * page being left: its offset is saved under that page's history entry.
 */
export function recordMainScroll( to: RouteLocationNormalized, from: RouteLocationNormalized, failure?: NavigationFailure | void ): void {
    if ( failure ) {
        return;
    }

    const main = mainScroller();
    if ( main && currentPosition !== null && from !== START_LOCATION ) {
        // A restore still in flight has not reached its offset yet; the target is where the member meant to be.
        saveOffset( offsetKey( currentPosition, from.fullPath ), pendingRestore?.top ?? main.scrollTop );
    }
    // A list that normalises its query on mount replaces the URL mid-restore; the restore carries on for the same page.
    if ( !isSamePageNavigation( to, from ) ) {
        cancelPendingRestore();
    }
    currentPosition = historyPosition();
}

/**
 * The router's `scrollBehavior`. vue-router only passes `savedPosition` for Back/Forward (and a reload), which is how
 * a restore is told apart from a push.
 */
export const mainScrollBehavior: RouterScrollBehavior = ( to, from, savedPosition ) => {
    // Stays where it is on both layouts.
    if ( isSamePageNavigation( to, from ) ) {
        return false;
    }

    cancelPendingRestore();

    const main = mainScroller();
    if ( !main ) {
        // Below lg the private shell scrolls the document. vue-router's saved position is right there, but it scrolls
        // once, before a lazy view and its data have arrived, and a still-short page clamped Back to near the top.
        const page = savedPosition ? shellDocumentScroller() : null;
        if ( page && savedPosition ) {
            restoreScroll( page, savedPosition.top, window );
            return false;
        }
        return savedPosition ?? { top: 0 };
    }

    if ( savedPosition ) {
        const position = historyPosition();
        const saved = position === null ? undefined : savedOffsets.get( offsetKey( position, to.fullPath ) );
        restoreScroll( main, saved ?? 0 );
    } else {
        resetScroll( main );
    }

    // <main> is handled above; the window does not scroll in the private shell from lg.
    return false;
};

/** Test helper: forget every saved offset and any restore in flight. */
export function resetMainScrollState(): void {
    cancelPendingRestore();
    savedOffsets.clear();
    currentPosition = null;
}
